import translate from '@translate';
import React, { FC, useState, useEffect } from 'react';
import FileUpload, { CustomFileActions, FilesPropType } from 'src/components/FileUpload';
import EntityFilesTable from 'src/components/FileUpload/EntityFilesTable';
import { OrderType } from 'src/types/order';
import Select from 'src/components/Select';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { FileType } from 'src/types/file';
import Switch from 'src/components/Switch';
import { sizes } from 'src/themes/default';
import { styled } from '@mui/material';
import { EditKeysType, SHIPPER_FILE_TYPES } from './constants';
import { FormErrorsType, OnChangeType } from '../../types/global';

export interface FilesBlockPropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: OrderType;
    editErrors: FormErrorsType;
    disableEdit: boolean;
    disableEditPrintFiles: boolean;
    documentTypesCodes: ConstantsCodeType[];
    summary?: boolean;
    isAttributeEditDisabled: (attributeKey: string) => boolean;
    skladonUser?: number;
}

export const Block = styled('div')(({ theme }) => ({
    '& .MuiTextField-root label': {
        display: 'none',
    },

    [theme.breakpoints.up('sm')]: {
        '& .MuiList-root .MuiListItemText-root .MuiTypography-body1': {
            maxWidth: 'calc(100% - 310px)',
        },
    },
}));

const FilesBlock: FC<FilesBlockPropsType> = (props) => {
    const [files, setFiles] = useState<FileType[]>([]);
    const [shipperFiles, setShipperFiles] = useState<FileType[]>([]);

    useEffect(() => {
        setFiles(props.editData.FILES?.filter((file) => !SHIPPER_FILE_TYPES.includes(file.type || '')) || []);
        setShipperFiles(props.editData.FILES?.filter((file) => SHIPPER_FILE_TYPES.includes(file.type || '')) || []);
    }, [props.editData.FILES]);

    const handleChange = (value: FilesPropType, error: boolean) => {
        props.onChange('FILES', [...shipperFiles, ...value.map((file) => ({ ...file }))], error);
    };

    const handleChangeType = (value: unknown, error: boolean, inputFile: FileType, inputName: string) => {
        props.onChange(
            'FILES',
            [...shipperFiles, ...files.map((file) => (file === inputFile ? { ...file, type: value } : file))],
            error,
            inputName
        );
    };

    const handleDeleteFile = (file: FileType) => {
        props.onChange(
            'FILES',
            [...shipperFiles, ...files.filter((oldFile) => oldFile !== file)],
            false
        );
    };

    const filesDisabled = props.isAttributeEditDisabled('FILES') || Boolean(props.summary);

    const customFileActions: CustomFileActions = (inputFile) => {
        const inputName = `file-type-${inputFile.fileName}`;
        return (
            <Select
                options={props.documentTypesCodes}
                value={inputFile.type}
                error={props.editErrors[inputName]}
                onChange={(value, error) => handleChangeType(value, error, inputFile, inputName)}
                name={inputName}
                required
                fullWidth
                disabled={filesDisabled}
            />
        );
    };

    return (
        <Block>
            <div style={{ paddingTop: sizes.body_PD, paddingBottom: sizes.body_PD }}>
                <Switch
                    value={!!props.editData.PRINT_FILES}
                    onChange={(value) => props.onChange('PRINT_FILES', value ? 1 : 0, false)}
                    label={translate('orderDetail.printFiles')}
                    disabled={
                        props.isAttributeEditDisabled('PRINT_FILES') ||
                        props.disableEditPrintFiles ||
                        props.summary
                    }
                />
            </div>
            <FileUpload
                files={files}
                onChange={(value, error) => handleChange(value, error)}
                name="FILES"
                error={props.editErrors.FILES}
                height={130}
                disabled={filesDisabled}
                multiple
                displayFilesList={false}
            />
            <EntityFilesTable
                files={files}
                disabled={filesDisabled}
                onDeleteFile={handleDeleteFile}
                skladonUser={props.skladonUser}
                customFileActions={customFileActions}
                testIdPrefix="order-files"
            />
        </Block>
    );
};

export default FilesBlock;
